import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import { Accordion } from "@/src/components/ui/accordion";
import { ToggleGroup, ToggleGroupItem } from "@/src/components/ui/toggle-group";
import { cn } from "@/src/lib/utils";

import BulkObjectRow from "./BulkObjectRow";
import {
  SEGMENT_ITEM_CLASS,
  SEGMENT_TRACK_CLASS,
} from "../form-fields/SegmentedField";

import type { JSX } from "react";

import type { BulkSummary } from "./bulkObjects";
import type { BulkObject, BulkObjectType } from "../../api/BulkAPI.types";

const ALL_TYPES_VALUE = "all";

const TYPE_ITEM_CLASS = cn(SEGMENT_ITEM_CLASS, "flex-none px-3");

interface BulkObjectListProps {
  objectList: BulkObject[];
  summary: BulkSummary;
}

function BulkObjectList({
  objectList,
  summary,
}: BulkObjectListProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");
  const [activeType, setActiveType] = useState<BulkObjectType | null>(null);

  const { byType } = summary;

  const hasSeveralTypes = byType.length > 1;

  const effectiveType = hasSeveralTypes
    ? (byType.find((entry) => entry.type === activeType)?.type ?? null)
    : (byType[0]?.type ?? null);

  const visibleObjectList =
    effectiveType === null
      ? objectList
      : objectList.filter((object) => object.type === effectiveType);

  const hasObjects = objectList.length > 0;

  return (
    <div className="flex flex-1 flex-col gap-3">
      {hasObjects ? (
        <ToggleGroup
          aria-label={t("filterByType")}
          className={cn(SEGMENT_TRACK_CLASS, "w-fit flex-wrap")}
          spacing={1}
          type="single"
          value={effectiveType ?? ALL_TYPES_VALUE}
          onValueChange={(next: string): void => {
            if (next === "") {
              return;
            }

            setActiveType(
              byType.find((entry) => entry.type === next)?.type ?? null,
            );
          }}
        >
          {hasSeveralTypes ? (
            <ToggleGroupItem
              className={TYPE_ITEM_CLASS}
              value={ALL_TYPES_VALUE}
            >
              {t("allObjects")} · {objectList.length}
            </ToggleGroupItem>
          ) : null}

          {byType.map((entry) => (
            <ToggleGroupItem
              key={entry.type}
              className={TYPE_ITEM_CLASS}
              value={entry.type}
            >
              {t(`types.${entry.type}`, {
                defaultValue: t("types.unknown"),
              })}{" "}
              · {entry.total}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : null}

      <div className="-m-1 min-h-48 grow basis-48 overflow-y-auto p-1">
        {hasObjects ? (
          <Accordion
            className="border-border rounded-xl border"
            type="multiple"
          >
            {visibleObjectList.map((object) => (
              <BulkObjectRow key={object.uuid} object={object} />
            ))}
          </Accordion>
        ) : (
          <p className="text-muted-foreground border-border rounded-xl border p-6 text-center text-sm">
            {t("noObjects")}
          </p>
        )}
      </div>
    </div>
  );
}

export default BulkObjectList;
